import { create } from 'zustand';

interface AuthState {
	accessToken: string | null;
	refreshToken: string | null;
	setAccessToken: (token: string) => void;
	setRefreshToken: (token: string) => void;
	getAccessToken: () => string | null;
	getRefreshToken: () => string | null;
	resetToken: () => void;
	isLogin: () => boolean;
}

type StoredTokens = {
	accessToken: string | null;
	refreshToken: string | null;
};

const TOKEN_STORAGE_KEY = 'token';

const persistTokens = (accessToken: string | null, refreshToken: string | null) => {
	if (!accessToken && !refreshToken) {
		localStorage.removeItem(TOKEN_STORAGE_KEY);
		return;
	}
	localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({ accessToken, refreshToken }));
};

const parseStoredTokens = (raw: string | null): StoredTokens => {
	if (!raw) {
		return { accessToken: null, refreshToken: null };
	}

	try {
		const parsed: unknown = JSON.parse(raw);

		if (typeof parsed === 'string') {
			return { accessToken: parsed, refreshToken: null };
		}

		if (parsed && typeof parsed === 'object') {
			const record = parsed as Record<string, unknown>;
			return {
				accessToken: typeof record.accessToken === 'string' ? record.accessToken : null,
				refreshToken: typeof record.refreshToken === 'string' ? record.refreshToken : null,
			};
		}
	} catch {
		// Legacy: access token stored as plain string (not JSON)
		return { accessToken: raw, refreshToken: null };
	}

	return { accessToken: null, refreshToken: null };
};

const getInitialTokens = (): StoredTokens => {
	const tokens = parseStoredTokens(localStorage.getItem(TOKEN_STORAGE_KEY));

	if (tokens.accessToken || tokens.refreshToken) {
		persistTokens(tokens.accessToken, tokens.refreshToken);
	}

	return tokens;
};

export const useTokenStore = create<AuthState>((set, get) => ({
	...getInitialTokens(),

	setAccessToken: (token: string) => {
		set({ accessToken: token });
		const { accessToken, refreshToken } = get();
		persistTokens(accessToken, refreshToken);
	},

	setRefreshToken: (token: string) => {
		set({ refreshToken: token });
		const { accessToken, refreshToken } = get();
		persistTokens(accessToken, refreshToken);
	},

	getAccessToken: () => get().accessToken,
	getRefreshToken: () => get().refreshToken,

	resetToken: () => {
		set({ accessToken: null, refreshToken: null });
		localStorage.removeItem(TOKEN_STORAGE_KEY);
	},

	isLogin: () => !!get().accessToken,
}));
